// libs
import { extractDummyModelName, extractFNumber } from 'libs';

// variables
import {
  classificationInformation,
  floorInformation,
  MAIN_OBJECT,
  NONE_TYPE,
  roomTypeClassification,
  siteInformation,
  structuralElementClassification
} from 'variables';

// libraries
import _ from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { Box3, Euler, Matrix4, Quaternion, Vector3 } from 'three';

/** Vector32를 일반 숫자로 변환하는 함수 */
export const getNumberFromVector32 = (vector32) => {
  return vector32 && typeof vector32.toNumber === 'function' ? vector32.toNumber() : vector32;
};

/**
 * 모델 구조 생성
 */
export const getModelStructure = (object) => {
  const controls = {};

  const updateVisibility = (obj) => {
    const objectName = obj.name || MAIN_OBJECT;

    controls[objectName] = obj.visible;

    if (obj.children && obj.children.length > 0) {
      obj.children.forEach((child) => {
        updateVisibility(child);
      });
    }
  };

  updateVisibility(object);

  return controls;
};

/**
 * 모델 트리 구조 생성
 */
export const getModelTreeStructure = (object) => {
  const createTreeNode = (obj) => {
    const nodeName = obj.name || MAIN_OBJECT;
    const node = {
      name: nodeName,
      visible: obj.visible,
      children: []
    };

    if (obj.children && obj.children.length > 0) {
      obj.children.forEach((child) => {
        node.children.push(createTreeNode(child));
      });
    }

    return node;
  };

  return createTreeNode(object);
};

/**
 * 동 정보 리스트 추출
 */
export const extractAllBuildingInfo = (object) => {
  const treeStructure = getModelTreeStructure(object);
  const buildingInfoSet = new Set();

  const findBuildingInfo = (node) => {
    if (node.name.includes('D') && /D\d{4}/.test(node.name)) {
      const match = node.name.match(/(D\d{4})/);
      if (match) {
        buildingInfoSet.add(match[1]);
      }
    } else {
      const match = node.name.match(/(\d{3})/);

      if (match && node.name.length === 3) {
        buildingInfoSet.add(match[1]);
      }
    }

    for (const child of node.children) {
      findBuildingInfo(child);
    }
  };

  findBuildingInfo(treeStructure);

  return Array.from(buildingInfoSet).sort((a, b) => a.localeCompare(b));
};

/**
 * 모델링 객체에서 건물과 층 정보 추출
 * TODO
 * S001 형식에 대한 처리 추가 필요
 */
export const extractBuildingAndFloorInfo = (object, siteType) => {
  const buildingFloorMap = siteType === 'S001' ? { D0309: new Set() } : {};

  const extractInfo = (obj) => {
    const buildingMatch = obj.name.match(/D(\d{4})/);
    const floorMatch = siteType === 'S001' ? extractFNumber(obj.name) : obj.name.match(/[FB](\d+)/);

    if (siteType === 'S001') {
      if (floorMatch) {
        buildingFloorMap['D0309'].add(floorMatch);
      }
    } else {
      if (buildingMatch) {
        const building = buildingMatch[0];

        if (!buildingFloorMap[building]) {
          buildingFloorMap[building] = new Set();
        }

        if (floorMatch) {
          buildingFloorMap[building].add(floorMatch[0]);
        }
      }
    }

    if (obj.children && obj.children.length > 0) {
      obj.children.forEach(extractInfo);
    }
  };

  const treeStructure = getModelTreeStructure(object);

  extractInfo(treeStructure);

  const sortFloors = (a, b) => {
    if (a === 'F00') return -1;

    if (b === 'F00') return 1;

    if (a === 'B00') return -1;

    if (b === 'B00') return 1;

    if (a.startsWith('B') && b.startsWith('B')) {
      return parseInt(a.slice(1)) - parseInt(b.slice(1));
    }

    if (a.startsWith('B')) return 1;

    if (b.startsWith('B')) return -1;

    return parseInt(b.slice(1)) - parseInt(a.slice(1));
  };

  const bundangSortFloors = (arr) => {
    return arr.sort((a, b) => {
      if (a[0] !== b[0]) {
        return b[0].localeCompare(a[0]);
      }

      const numA = parseInt(a.slice(1));
      const numB = parseInt(b.slice(1));

      if (a[0] === 'F') {
        return numB - numA;
      }
      return numA - numB;
    });
  };

  const bundangSortFloorsOptions = (arr) => {
    return arr.sort((a, b) => {
      if (a.key === MAIN_OBJECT) return -1;
      if (b.key === MAIN_OBJECT) return 1;

      if (a.key[0] !== b.key[0]) {
        return b.key[0].localeCompare(a.key[0]);
      }

      const numA = parseInt(a.key.slice(1));
      const numB = parseInt(b.key.slice(1));

      if (a.key[0] === 'F') {
        return numB - numA;
      }

      return numA - numB;
    });
  };

  const result = Object.keys(buildingFloorMap).map((buildingCode) => {
    let floorList = Array.from(buildingFloorMap[buildingCode]);

    if (siteType === 'S001') {
      bundangSortFloors(floorList);
    } else {
      floorList.sort(sortFloors);
    }

    let maxFloor, minFloor;

    if (buildingCode === 'D0000') {
      maxFloor = 'F00';
      minFloor = floorList[floorList.length - 1];
    } else {
      maxFloor = floorList.find((f) => f.startsWith('F')) || floorList[0];
      minFloor =
        floorList.filter((f) => f.startsWith('F')).pop() || floorList[floorList.length - 1];
    }

    let floorOptions = [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '전체'
      },
      ...floorList.map((floor) => ({
        id: uuid(),
        key: floor,
        value: floor,
        label: floor
      })),
      {
        id: uuid(),
        key: 'allHidden',
        value: 'allHidden',
        label: '숨김'
      }
    ].sort((a, b) => sortFloors(a.key, b.key));

    if (buildingCode === 'D0000') {
      floorList = floorList.filter((floor) => floor === 'F00' || floor.startsWith('B'));

      floorOptions = [
        {
          id: uuid(),
          key: MAIN_OBJECT,
          value: MAIN_OBJECT,
          label: '전체'
        },
        ...floorOptions
          .filter((option) => option.key === 'F00' || option.key.startsWith('B'))
          .sort((a, b) => sortFloors(a.key, b.key)),
        {
          id: uuid(),
          key: 'allHidden',
          value: 'allHidden',
          label: '숨김'
        }
      ];
    }

    return {
      buildingCode,
      floorList,
      minFloor,
      maxFloor,
      floorLength: floorList.length,
      floorOptions: siteType === 'S001' ? bundangSortFloorsOptions(floorOptions) : floorOptions
    };
  });

  const allFloors = Array.from(
    new Set(Object.values(buildingFloorMap).flatMap((floors) => Array.from(floors)))
  );

  allFloors.sort(sortFloors);

  const sortAllFloors = floorSort(allFloors);

  const allBuildingsInfo = {
    buildingCode: 'All',
    floorList: sortAllFloors,
    minFloor: sortAllFloors[sortAllFloors.length - 1],
    maxFloor: sortAllFloors[0],
    floorLength: sortAllFloors.length,
    floorOptions: [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '전체'
      },
      ...sortAllFloors.map((floor) => ({
        id: uuid(),
        key: floor,
        value: floor,
        label: floor
      }))
    ]
  };

  return [allBuildingsInfo, ...result].map((item) => ({ ...item, currentFloor: MAIN_OBJECT }));
};

/**
 * 모델링의 더미 버전 건물과 층 정보 추출
 */
export const extractBuildingAndFloorInfoDummyVersion = (object) => {
  const buildingFloorMap = {};

  const extractInfo = (obj, isGroundItem = false) => {
    const buildingMatch = obj.name.match(/(\d{3})/);
    const floorMatch = obj.name.match(/([FB](\d+)|BF(\d+))/);

    const isGround = obj.name.match(/Ground/);

    // BF로 시작하는 경우 처리
    if (obj.name.startsWith('BF') && floorMatch) {
      const building = 'Basement';

      if (!buildingFloorMap[building]) {
        buildingFloorMap[building] = new Set();
      }

      let floorCode = floorMatch[0];
      if (floorCode.startsWith('BF')) {
        floorCode = floorCode.replace('BF', 'B');
      }

      buildingFloorMap[building].add(floorCode);

      if (obj.children && obj.children.length > 0) {
        obj.children.forEach((el) => extractInfo(el, isGround));
      }

      return;
    }

    if (buildingMatch) {
      const building = buildingMatch[0];

      if (!buildingFloorMap[building]) {
        buildingFloorMap[building] = new Set();
      }

      if (floorMatch) {
        let floorCode = floorMatch[0];

        if (floorCode.startsWith('BF')) {
          floorCode = floorCode.replace('BF', 'B');
        }
        buildingFloorMap[building].add(floorCode);
      } else {
        buildingFloorMap[building].add('RF');
      }
    }

    if (isGroundItem) {
      const building = 'Ground';

      if (!buildingFloorMap[building]) {
        buildingFloorMap[building] = new Set();
      }

      if (obj.name === 'Ground') {
        buildingFloorMap[building].add('G00');
      } else if (obj.name === 'Fence') {
        buildingFloorMap[building].add('G01');
      } else {
        buildingFloorMap[building].add(obj.name);
      }
    }

    if (obj.children && obj.children.length > 0) {
      obj.children.forEach((el) => extractInfo(el, isGround));
    }
  };

  const treeStructure = getModelTreeStructure(object);

  extractInfo(treeStructure);

  const sortFloors = (a, b) => {
    if (a === 'F00') return -1;
    if (b === 'F00') return 1;

    if (a === 'B00') return -1;
    if (b === 'B00') return 1;

    // G 코드 정렬 (G02, G01, G00... 내림차순으로)
    if (a.startsWith('G') && b.startsWith('G')) {
      return parseInt(b.slice(1)) - parseInt(a.slice(1));
    }

    // G 코드가 있을 때 다른 타입과의 우선순위
    if (a.startsWith('G')) return -1; // G 코드가 가장 앞에
    if (b.startsWith('G')) return 1;

    if (a.startsWith('B') && b.startsWith('B')) {
      return parseInt(a.slice(1)) - parseInt(b.slice(1));
    }

    if (a.startsWith('B')) return 1;
    if (b.startsWith('B')) return -1;

    return parseInt(b.slice(1)) - parseInt(a.slice(1));
  };

  const bundangSortFloorsOptions = (arr) => {
    return arr.sort((a, b) => {
      if (a.key === MAIN_OBJECT) return -1;
      if (b.key === MAIN_OBJECT) return 1;

      // G 코드들끼리 비교
      if (a.key.startsWith('G') && b.key.startsWith('G')) {
        const numA = parseInt(a.key.slice(1));
        const numB = parseInt(b.key.slice(1));
        return numB - numA; // G 코드는 내림차순 (G02, G01, G00)
      }

      // G 코드와 다른 타입 비교 (G 코드가 가장 앞에)
      if (a.key.startsWith('G')) return -1;
      if (b.key.startsWith('G')) return 1;

      if (a.key[0] !== b.key[0]) {
        return b.key[0].localeCompare(a.key[0]);
      }

      const numA = parseInt(a.key.slice(1));
      const numB = parseInt(b.key.slice(1));

      if (a.key[0] === 'F') {
        return numB - numA;
      }

      return numA - numB;
    });
  };

  const extractFloorNumber = (floor) => {
    if (floor === 'RF') return 0;

    const match = floor.match(/F(\d+)/);

    if (match) {
      return parseInt(match[1], 10);
    }

    return 0;
  };

  const result = Object.keys(buildingFloorMap).map((buildingCode) => {
    let floorList = Array.from(buildingFloorMap[buildingCode]);

    floorList.sort(sortFloors);

    const mappedFloors = floorList.map((floor) => {
      let value;

      if (floor === 'RF') {
        const highestFloorNum = extractFloorNumber(floorList[1]);
        value = `F${highestFloorNum + 1}`;
      } else {
        value = floor;
      }

      // Ground 건물일 때 특별한 라벨 처리
      let label = floor;
      if (buildingCode === 'Ground') {
        if (floor === 'G00') {
          label = 'Ground';
        } else if (floor === 'G01') {
          label = 'Fence';
        }
      }

      return {
        id: uuid(),
        key: value,
        value: value,
        label: label
      };
    });

    let maxFloor, minFloor;

    const groundFloors = mappedFloors.filter((f) => f.value.startsWith('F'));
    const basementFloors = mappedFloors.filter((f) => f.value.startsWith('B'));

    if (groundFloors.length > 0) {
      maxFloor = groundFloors[0].value;
      minFloor = groundFloors[groundFloors.length - 1].value;
    } else if (basementFloors.length > 0) {
      maxFloor = basementFloors[0].value;
      minFloor = basementFloors[basementFloors.length - 1].value;
    } else {
      maxFloor = mappedFloors.length > 0 ? mappedFloors[0].value : '';
      minFloor = mappedFloors.length > 0 ? mappedFloors[mappedFloors.length - 1].value : '';
    }

    let floorOptions = [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '전체'
      },
      ...mappedFloors,
      {
        id: uuid(),
        key: 'allHidden',
        value: 'allHidden',
        label: '숨김'
      }
    ].sort((a, b) => sortFloors(a.key, b.key));

    if (buildingCode === 'D0000') {
      floorList = floorList.filter((floor) => floor === 'F00' || floor.startsWith('B'));

      floorOptions = [
        {
          id: uuid(),
          key: MAIN_OBJECT,
          value: MAIN_OBJECT,
          label: '전체'
        },
        ...floorOptions
          .filter((option) => option.key === 'F00' || option.key.startsWith('B'))
          .sort((a, b) => sortFloors(a.key, b.key)),
        {
          id: uuid(),
          key: 'allHidden',
          value: 'allHidden',
          label: '숨김'
        }
      ];
    }

    if (buildingCode === 'Ground') {
      floorOptions = [
        {
          id: uuid(),
          key: MAIN_OBJECT,
          value: MAIN_OBJECT,
          label: '전체'
        },
        ...mappedFloors,
        {
          id: uuid(),
          key: 'allHidden',
          value: 'allHidden',
          label: '숨김'
        }
      ].sort((a, b) => sortFloors(a.key, b.key));
    }

    return {
      buildingCode,
      floorList: mappedFloors,
      minFloor,
      maxFloor,
      floorLength: mappedFloors.length,
      floorOptions: bundangSortFloorsOptions(floorOptions)
    };
  });

  const allFloors = Array.from(
    new Set(Object.values(buildingFloorMap).flatMap((floors) => Array.from(floors)))
  );

  allFloors.sort(sortFloors);

  const sortAllFloors = floorSort(allFloors);

  const allBuildingsInfo = {
    buildingCode: 'All',
    floorList: sortAllFloors,
    minFloor: sortAllFloors[sortAllFloors.length - 1],
    maxFloor: sortAllFloors[0],
    floorLength: sortAllFloors.length,
    floorOptions: [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '전체'
      },
      ...sortAllFloors.map((floor) => ({
        id: uuid(),
        key: floor,
        value: floor,
        label: floor
      }))
    ]
  };

  return [allBuildingsInfo, ...result].map((item) => ({
    ...item,
    currentFloor: MAIN_OBJECT
  }));
};

/**
 * Dummy building/floor extraction that supports string building names.
 */
export const extractBuildingAndFloorInfoDummyVersionByName = (object, floorOrderConfig = null) => {
  const buildingFloorMap = {};
  const normalizeOrderKey = (key) => {
    if (!key) return key;

    if (key.startsWith('BF')) {
      return key.replace('BF', 'B');
    }

    const match = key.match(/^([FB])(\d+)$/);
    if (match) {
      return `${match[1]}${match[2].padStart(2, '0')}`;
    }

    return key;
  };

  const resolveFloorOrderList = (buildingCode) => {
    if (!floorOrderConfig) return null;

    if (Array.isArray(floorOrderConfig)) return floorOrderConfig;

    if (typeof floorOrderConfig === 'object') {
      const buildingList = floorOrderConfig.buildings?.[buildingCode];
      if (buildingList) return buildingList;
      return floorOrderConfig.default || null;
    }

    return null;
  };

  const createFloorOrderMap = (orderList) => {
    if (!orderList) return null;
    return new Map(orderList.map((item) => [normalizeOrderKey(item.floor), item.sortingOrder]));
  };

  const normalizeFloorCode = (name) => {
    if (/^RF\d*/.test(name)) {
      return 'RF';
    }

    const match = name.match(/^(F\d+|B\d+|BF\d+)/);
    if (!match) return name;

    let code = match[0];
    if (code.startsWith('BF')) {
      code = code.replace('BF', 'B');
    }
    return code;
  };

  const addFloorToBuilding = (buildingName, floorName) => {
    if (!buildingName) return;

    if (!buildingFloorMap[buildingName]) {
      buildingFloorMap[buildingName] = new Set();
    }

    const floorCode = normalizeFloorCode(floorName);
    buildingFloorMap[buildingName].add(floorCode);
  };

  const extractFromTree = (obj) => {
    const buildings = obj && obj.children ? obj.children : [];

    buildings.forEach((buildingNode) => {
      const buildingName = buildingNode.name;
      const floors = buildingNode.children || [];
      const isNumericBuilding = /^\d{3}$/.test(buildingName);
      const hasOnlyBasementFloors =
        floors.length > 0 && floors.every((floorNode) => floorNode.name.startsWith('BF'));

      if (buildingName === 'Ground') {
        if (!buildingFloorMap[buildingName]) {
          buildingFloorMap[buildingName] = new Set();
        }
        floors.forEach((floorNode) => {
          if (floorNode.name === 'Ground') {
            addFloorToBuilding(buildingName, 'G00');
          } else if (floorNode.name === 'Fence') {
            addFloorToBuilding(buildingName, 'G01');
          } else {
            addFloorToBuilding(buildingName, floorNode.name);
          }
        });
        return;
      }

      if (!hasOnlyBasementFloors && !buildingFloorMap[buildingName]) {
        buildingFloorMap[buildingName] = new Set();
      }

      floors.forEach((floorNode) => {
        const floorName = floorNode.name;
        const targetBuilding =
          floorName.startsWith('BF') || (isNumericBuilding && hasOnlyBasementFloors)
            ? 'Basement'
            : buildingName;
        addFloorToBuilding(targetBuilding, floorName);
      });
    });
  };

  const treeStructure = getModelTreeStructure(object);

  extractFromTree(treeStructure);

  const sortFloors = (a, b) => {
    if (a === 'RF') return -1;
    if (b === 'RF') return 1;
    if (a === 'F00') return -1;
    if (b === 'F00') return 1;

    if (a === 'B00') return -1;
    if (b === 'B00') return 1;

    // G codes should be sorted descending (G02, G01, G00...)
    if (a.startsWith('G') && b.startsWith('G')) {
      return parseInt(b.slice(1)) - parseInt(a.slice(1));
    }

    if (a.startsWith('G')) return -1;
    if (b.startsWith('G')) return 1;

    if (a.startsWith('B') && b.startsWith('B')) {
      return parseInt(a.slice(1)) - parseInt(b.slice(1));
    }

    if (a.startsWith('B')) return 1;
    if (b.startsWith('B')) return -1;

    return parseInt(b.slice(1)) - parseInt(a.slice(1));
  };

  const sortFloorsWithOrder = (a, b, orderMap) => {
    if (!orderMap) return sortFloors(a, b);

    const normalizedA = normalizeOrderKey(a);
    const normalizedB = normalizeOrderKey(b);
    const orderA = orderMap.has(normalizedA) ? orderMap.get(normalizedA) : null;
    const orderB = orderMap.has(normalizedB) ? orderMap.get(normalizedB) : null;

    if (orderA !== null && orderB !== null) return orderB - orderA;
    if (orderA !== null) return -1;
    if (orderB !== null) return 1;

    return sortFloors(a, b);
  };

  const sortFloorOptions = (a, b, orderMap) => {
    if (a.key === MAIN_OBJECT) return -1;
    if (b.key === MAIN_OBJECT) return 1;
    if (a.key === 'allHidden') return 1;
    if (b.key === 'allHidden') return -1;

    return orderMap ? sortFloorsWithOrder(a.key, b.key, orderMap) : sortFloors(a.key, b.key);
  };

  const bundangSortFloorsOptions = (arr) => {
    return arr.sort((a, b) => {
      if (a.key === MAIN_OBJECT) return -1;
      if (b.key === MAIN_OBJECT) return 1;

      if (a.key === 'RF') return -1;
      if (b.key === 'RF') return 1;

      if (a.key.startsWith('G') && b.key.startsWith('G')) {
        const numA = parseInt(a.key.slice(1));
        const numB = parseInt(b.key.slice(1));
        return numB - numA;
      }

      if (a.key.startsWith('G')) return -1;
      if (b.key.startsWith('G')) return 1;

      if (a.key[0] !== b.key[0]) {
        return b.key[0].localeCompare(a.key[0]);
      }

      const numA = parseInt(a.key.slice(1));
      const numB = parseInt(b.key.slice(1));

      if (a.key[0] === 'F') {
        return numB - numA;
      }

      return numA - numB;
    });
  };

  const result = Object.keys(buildingFloorMap).map((buildingCode) => {
    const floorOrderList = resolveFloorOrderList(buildingCode);
    const floorOrderMap = createFloorOrderMap(floorOrderList);
    let floorList = Array.from(buildingFloorMap[buildingCode]);

    floorList.sort(floorOrderMap ? (a, b) => sortFloorsWithOrder(a, b, floorOrderMap) : sortFloors);

    const mappedFloors = floorList.map((floor) => {
      let value;

      value = floor;

      let label = floor;
      if (floor === 'RF') {
        label = '옥상';
      }
      if (buildingCode === 'Ground') {
        if (floor === 'G00') {
          label = 'Ground';
        } else if (floor === 'G01') {
          label = 'Fence';
        }
      }

      let floorLevel;
      if (floorOrderMap) {
        const normalizedFloor = normalizeOrderKey(floor);
        floorLevel = floorOrderMap.has(normalizedFloor)
          ? floorOrderMap.get(normalizedFloor)
          : parseInt(floor.slice(1)) || 0;
      } else {
        floorLevel = parseInt(floor.slice(1)) || 0;
      }

      return {
        id: uuid(),
        key: value,
        value: value,
        label: label,
        floorLevel: floorLevel
      };
    });

    let maxFloor, minFloor;

    const groundFloors = mappedFloors.filter((f) => f.value.startsWith('F'));
    const basementFloors = mappedFloors.filter((f) => f.value.startsWith('B'));

    if (groundFloors.length > 0) {
      maxFloor = groundFloors[0].value;
      minFloor = groundFloors[groundFloors.length - 1].value;
    } else if (basementFloors.length > 0) {
      maxFloor = basementFloors[0].value;
      minFloor = basementFloors[basementFloors.length - 1].value;
    } else {
      maxFloor = mappedFloors.length > 0 ? mappedFloors[0].value : '';
      minFloor = mappedFloors.length > 0 ? mappedFloors[mappedFloors.length - 1].value : '';
    }

    let floorOptions = [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '\uc804\uccb4'
      },
      ...mappedFloors,
      {
        id: uuid(),
        key: 'allHidden',
        value: 'allHidden',
        label: '\uc228\uae40'
      }
    ].sort((a, b) => sortFloorOptions(a, b, floorOrderMap));

    if (buildingCode === 'D0000') {
      floorList = floorList.filter((floor) => floor === 'F00' || floor.startsWith('B'));

      floorOptions = [
        {
          id: uuid(),
          key: MAIN_OBJECT,
          value: MAIN_OBJECT,
          label: '전체'
        },
        ...floorOptions
          .filter((option) => option.key === 'F00' || option.key.startsWith('B'))
          .sort((a, b) => sortFloorOptions(a, b, floorOrderMap)),
        {
          id: uuid(),
          key: 'allHidden',
          value: 'allHidden',
          label: '숨김'
        }
      ];
    }

    if (buildingCode === 'Ground') {
      floorOptions = [
        {
          id: uuid(),
          key: MAIN_OBJECT,
          value: MAIN_OBJECT,
          label: '\uc804\uccb4'
        },
        ...mappedFloors,
        {
          id: uuid(),
          key: 'allHidden',
          value: 'allHidden',
          label: '\uc228\uae40'
        }
      ].sort((a, b) => sortFloorOptions(a, b, floorOrderMap));
    }

    return {
      buildingCode,
      floorList: mappedFloors,
      minFloor,
      maxFloor,
      floorLength: mappedFloors.length,
      floorOptions: floorOrderMap ? floorOptions : bundangSortFloorsOptions(floorOptions)
    };
  });

  const allFloors = Array.from(
    new Set(Object.values(buildingFloorMap).flatMap((floors) => Array.from(floors)))
  );

  const allOrderList = resolveFloorOrderList('All');
  const allOrderMap = createFloorOrderMap(allOrderList);

  allFloors.sort(allOrderMap ? (a, b) => sortFloorsWithOrder(a, b, allOrderMap) : sortFloors);

  const sortAllFloors = allFloors;

  const allBuildingsInfo = {
    buildingCode: 'All',
    floorList: sortAllFloors,
    minFloor: sortAllFloors[sortAllFloors.length - 1],
    maxFloor: sortAllFloors[0],
    floorLength: sortAllFloors.length,
    floorOptions: [
      {
        id: uuid(),
        key: MAIN_OBJECT,
        value: MAIN_OBJECT,
        label: '\uc804\uccb4'
      },
      ...sortAllFloors.map((floor) => ({
        id: uuid(),
        key: floor,
        value: floor,
        label: floor === 'RF' ? '옥상' : floor
      }))
    ]
  };

  return [allBuildingsInfo, ...result].map((item) => ({
    ...item,
    currentFloor: MAIN_OBJECT
  }));
};

/**
 * 층 정보 정렬
 * F + 숫자, B + 숫자 순서로 정렬함
 */
export const floorSort = (floorList) => {
  if (!_.isArray(floorList) || floorList.length < 1) return [];

  const mainObject = floorList.find((item) =>
    typeof item === 'object' ? item.key === MAIN_OBJECT : item === MAIN_OBJECT
  );

  const filteredList = floorList.filter((item) =>
    typeof item === 'object' ? item.key !== MAIN_OBJECT : item !== MAIN_OBJECT
  );

  const sortResult = _.sortBy(filteredList, [
    (item) => {
      const key = typeof item === 'object' ? item.key : item;
      return key[0] === 'F' ? 0 : 1;
    },
    (item) => {
      const key = typeof item === 'object' ? item.key : item;
      const num = parseInt(key.slice(1), 10);
      return key[0] === 'F' ? -num : num;
    }
  ]);

  if (mainObject) {
    return [mainObject, ...sortResult];
  }

  return sortResult;
};

/**
 * 건물 정보 포맷 함수
 */
export const formatBuildingData = (buildings, siteCode) => {
  if (!Array.isArray(buildings)) return [];

  const isSpecialSite = siteCode === 'S001' || siteCode === 'S002';

  return buildings.map((building) => {
    /** ---------------------------
     * building code 처리
     * -------------------------- */
    const formattedCode = isSpecialSite
      ? `D${String(building.code).padStart(4, '0')}`
      : building.code;

    /** ---------------------------
     * maxFloorCode 처리
     * -------------------------- */
    let formattedMaxFloor;

    if (building.maxFloorCode) {
      // F + 숫자 형태인 경우만 formatting (F1 -> F01, F10 -> F10)
      if (/^F\d+$/.test(building.maxFloorCode)) {
        const floorNumber = building.maxFloorCode.slice(1); // F 제거
        formattedMaxFloor = floorNumber.length === 1 ? `F0${floorNumber}` : `F${floorNumber}`;
      } else {
        // 그 외 (RF, B1, PIT 등)는 그대로 사용
        formattedMaxFloor = building.maxFloorCode;
      }
    }

    return {
      code: formattedCode,
      currentFloor: formattedMaxFloor
    };
  });
};

/**
 * 건물 코드를 원하는 형태로 변환하여 추출하는 함수
 */
export const extractBuildingName = (buildingCode) => {
  if (!buildingCode) return buildingCode;

  if (buildingCode.startsWith('D')) {
    const number = parseInt(buildingCode.slice(1));

    return `${number}동`;
  }

  return buildingCode;
};

/**
 * 호실 코드를 원하는 형태로 변환하는 함수
 */
export const extractRoomName = (roomCode) => {
  if (!roomCode) return roomCode;

  if (roomCode.startsWith('R')) {
    const number = parseInt(roomCode.slice(1));

    return `${number}호`;
  }

  if (roomCode === 'CSA00') {
    return '공용공간';
  }

  return roomCode;
};

/**
 * 파주
 * 객체 구조 이름 조합 함수
 */
export const formatRoomInfoPaju = (code) => {
  /** 정규표현식을 사용하여 코드 파싱 */
  const match = code.split('_');

  if (!match) return `${code} - Invalid code format`;

  const [siteCode, constructionType, buildingCode, floorCode, roomCode, structureType, number] =
    match;

  /** 현장 정보 찾기 */
  const siteInfo = siteInformation.find((s) => s.siteType === siteCode);
  const siteName = siteInfo ? siteInfo.siteKo : siteCode;

  /** 구분 정보 찾기 */
  const classInfo = classificationInformation.find(
    (c) => c.classificationType === constructionType
  );
  const classificationName = classInfo ? classInfo.classificationKo : constructionType;

  /** 동 정보 찾기 */
  const buildingName = extractBuildingName(buildingCode);

  /** 층 정보 찾기 */
  let floorName;

  if (floorCode && floorCode.startsWith('B')) {
    /** 지하층 처리 */
    const floorInfo = floorInformation.find((f) => f.floorType === floorCode);
    floorName = floorInfo ? floorInfo.floorKo : `지하${floorCode.slice(1)}층`;
  } else {
    /** 지상층 처리 */
    const floorNumber = floorCode?.slice(1);
    floorName = `${floorNumber}층`;
  }

  /** 호실 정보 찾기 */
  const roomName = extractRoomName(roomCode);

  /** 구조물 정보 찾기 */
  const structureInfo = [...structuralElementClassification, ...roomTypeClassification].find(
    (s) => s.structureType === structureType
  );
  const structureName = structureInfo ? structureInfo.structureNameKo : structureType;

  /** 결과 조합 */
  const fullName = number
    ? `${buildingName} ${floorName} ${roomName} ${structureName} ${number}`
    : `${buildingName} ${floorName} ${roomName} ${structureName}`;

  return {
    fullName,
    siteCode,
    constructionType,
    classificationName,
    buildingCode,
    floorCode,
    floorName,
    roomCode,
    roomName,
    structureType,
    structureName,
    number: number || null
  };
};

/**
 * 객체 구조 이름 업데이트 함수
 */
export const updateObjectStructureName = (child, siteCode) => {
  const isPaju = siteCode === 'S002' ? true : false;

  if (!child.isMesh) {
    console.error('child is not mesh');

    return {
      isSuccess: false,
      structureArr: []
    };
  }

  let structureArr = [];
  const name = child.name;

  if (!isPaju) {
    const structureType = roomTypeClassification.filter(
      (item) => item.type && item.type === 'B-2-0'
    );

    const structureName = structureType.find((item) => {
      const type = name.includes('SITE')
        ? 'SITE'
        : name.includes('BRFSL')
          ? 'BRFSL'
          : name.includes('BRFWL')
            ? 'BRFWL'
            : name.match(/[A-Z]+/g)?.[1] || '';

      if (siteCode !== 'S001') {
        const extractMName = extractDummyModelName(name);

        const isExist = item.structureName.includes(extractMName);

        if (isExist) {
          return item;
        }

        return null;
      }

      return type === item.structureType || type === item.structureName;
    });

    const findParentUntilMainObject = (obj) => {
      if (!obj.parent) {
        return obj;
      }

      if (obj.parent.name === MAIN_OBJECT) {
        return obj;
      }

      return findParentUntilMainObject(obj.parent);
    };

    const parentUntilMainObject = findParentUntilMainObject(child);

    if (structureName) {
      const extractFNum = extractFNumber(name) || name;

      child.userData = {
        ...child.userData,
        id: uuid(),
        name,
        structureName: structureName.structureName,
        structureNameKo: structureName.structureNameKo,
        structureVisible: true,
        structureCode: {
          siteCode: siteCode,
          buildingCode: siteCode === 'S001' ? 'D0309' : parentUntilMainObject.name,
          floorCode: extractFNum,
          ...structureName
        }
      };

      /**
       * fileName       : modelUtils.js
       * author         : KDONG1224
       * date           : 2025-04-07
       * description    : 더미체계로 모델링이 변경됨에 따라 Ground 객체에 대한 처리 추가
       *                  (현재는 S003 - 제일건설 곤지암 현장에 맞춰서 수정됨)
       * ===========================================================
       * DATE              AUTHOR             NOTE
       * -----------------------------------------------------------
       * 2025-04-14        KDONG1224       Ground 객체에 대한 처리 추가
       */
      if (child.name === 'Ground') {
        child.parent.userData = {
          ...child.parent.userData,
          id: uuid(),
          name,
          structureName: structureName.structureName,
          structureNameKo: structureName.structureNameKo,
          structureVisible: true,
          roomName: 'Ground',
          structureCode: {
            siteCode: siteCode,
            buildingCode: siteCode === 'S001' ? 'D0309' : parentUntilMainObject.name,
            floorCode: MAIN_OBJECT,
            ...structureName
          }
        };
      }
    } else {
      const extractFNum = extractFNumber(name) || name;

      child.userData = {
        ...child.userData,
        id: uuid(),
        name,
        structureName: NONE_TYPE,
        structureNameKo: '-',
        structureVisible: false,
        fontSize: 6,
        structureCode: {
          siteCode: siteCode,
          buildingCode: siteCode === 'S001' ? 'D0309' : parentUntilMainObject.name,
          floorCode: extractFNum
        }
      };
    }

    return {
      isSuccess: true,
      structureArr: [...structureArr]
    };
  } else {
    const exportRoomInfo = formatRoomInfoPaju(child.name);

    const findStructure = [...structuralElementClassification, ...roomTypeClassification].find(
      (item) => item.structureType === exportRoomInfo.structureType
    );

    child.userData = {
      ...child.userData,
      id: uuid(),
      name,
      structureName: exportRoomInfo.structureName || 'NONE',
      structureNameKo: exportRoomInfo.structureNameKo || '-',
      structureVisible: true,
      fontSize: findStructure?.fontSize || 0,
      structureCode: {
        siteCode: exportRoomInfo.siteCode,
        constructionType: exportRoomInfo.constructionType,
        buildingCode: exportRoomInfo.buildingCode,
        floorCode: exportRoomInfo.floorCode,
        roomCode: exportRoomInfo.roomCode,
        structureType: exportRoomInfo.structureType,
        number: exportRoomInfo.number
      }
    };

    structureArr.push(child.userData);

    return {
      isSuccess: true,
      structureArr: [...structureArr]
    };
  }
};

/**
 * 객체의 중심 위치를 구하는 함수
 */
export const getMeshCenterPosition = (child) => {
  if (!child.isMesh || child.geometry === undefined) {
    console.error('child is not mesh or geometry is undefined');

    return false;
  }

  // 메시의 바운딩 박스 계산
  child.geometry.computeBoundingBox();
  const boundingBox = child.geometry.boundingBox.clone();

  // 바운딩 박스의 중심점 계산
  const center = new Vector3();
  boundingBox.getCenter(center);

  // 로컬 중심점을 월드 좌표로 변환
  const worldPosition = new Vector3();
  child.getWorldPosition(worldPosition);

  // 월드 좌표에 로컬 중심점 오프셋 적용
  worldPosition.add(center);

  const centerPosition = {
    x: worldPosition.x - 8,
    y: worldPosition.y,
    z: worldPosition.z
  };

  const eulerRotation = getLabelEulerRotation(centerPosition);

  child.userData = {
    ...child.userData,
    center: centerPosition,
    rotation: eulerRotation
  };
};

/**
 * 그룹의 중심 위치를 구하는 함수
 */
export const getGroupCenterPosition = (group) => {
  if (!group.isGroup) {
    console.error('parameter is not a group');
    return false;
  }

  const totalBoundingBox = new Box3();

  let hasMesh = false;

  group.traverse((child) => {
    if (child.isMesh && child.geometry) {
      child.geometry.computeBoundingBox();
      const meshBoundingBox = child.geometry.boundingBox.clone();

      meshBoundingBox.applyMatrix4(child.matrixWorld);

      totalBoundingBox.union(meshBoundingBox);
      hasMesh = true;
    }
  });

  if (!hasMesh) {
    console.error('no meshes found in group');
    return false;
  }

  const center = new Vector3();

  totalBoundingBox.getCenter(center);

  const height = totalBoundingBox.max.y - totalBoundingBox.min.y;

  const centerPosition = {
    x: center.x - 8,
    y: center.y,
    z: center.z
  };

  const eulerRotation = getLabelEulerRotation(centerPosition);

  group.userData = {
    ...group.userData,
    center: centerPosition,
    rotation: eulerRotation,
    height,
    displayName: formatBuildingNumber(group.name)
  };
};

/**
 * 객체에 맞게 라벨 rotation 설정
 */
export const getLabelEulerRotation = (center) => {
  if (!center) {
    console.error('center is not defined');

    return [0, 0, 0];
  }

  const centerVec = new Vector3(center.x, center.y, center.z);
  const upVector = new Vector3(0, 1, 0);
  const normalVector = new Vector3(0, 0, 1);

  const matrix = new Matrix4().lookAt(centerVec, upVector, normalVector);
  const quaternion = new Quaternion().setFromRotationMatrix(matrix);

  const euler = new Euler().setFromQuaternion(quaternion);

  return {
    x: euler.x,
    y: euler.y,
    z: euler.z
  };
};

/**
 * 분당 object Name 변경 함수
 */
export const updateBundangChildName = (child, name, siteCode) => {
  if (name === '0Y4Avp$ObAPRr_ReiMexPo') {
    child.name = 'F05ST001';
  }

  if (name.startsWith('SITE')) {
    child.name = `F00SE${name.slice(4)}`;

    if (child.parent) {
      child.parent.name = 'F00_000';
    }
  }

  if (name.startsWith('BRF')) {
    child.name = `F00${name.slice(3)}`;

    if (child.parent) {
      child.parent.name = 'F00_000';
    }
  }
};

/**
 * 빌딩이름을 한글로 바꿔주는 함수
 */
export const formatBuildingNumber = (groupName) => {
  const match = groupName.match(/D0(\d+)$/);

  if (match && match[1]) {
    return `${match[1]}동`;
  }

  return groupName;
};
